/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React from 'react';
import { useNavigate } from 'react-router-dom';
import './AppInfo.css';

const AppInfo: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="app-info-container">
      <div className="app-info-box">
        <h1>Bid Generator</h1>
        <h2>About This Application</h2>

        <div className="info-content">
          <section className="info-section">
            <h3>Overview</h3>
            <p>
              Bid Generator is a comprehensive application designed to streamline the creation,
              management, and submission of business proposals and bids. The system provides
              tools for managing reusable component templates, assembling complex bid packages,
              tracking submission status, and generating professional bid documents.
            </p>
          </section>

          <section className="info-section">
            <h3>Key Features</h3>
            <ul>
              <li><strong>Component Management:</strong> Upload and organize reusable component templates with front page templates</li>
              <li><strong>Bid Creation:</strong> Assemble bids from multiple components with customizable content</li>
              <li><strong>Rich Text Editing:</strong> Create custom cover sheets and front pages using TinyMCE editor</li>
              <li><strong>Component Generation:</strong> Automatically merge components into professional bid packages</li>
              <li><strong>Company Calendar:</strong> Track bid due dates and submission status across your team</li>
              <li><strong>Submission Tracking:</strong> Monitor which bids have been submitted and accepted</li>
              <li><strong>Reporting:</strong> Generate activity reports and team performance analytics</li>
              <li><strong>Role-Based Access:</strong> User, Manager, and Admin roles with appropriate permissions</li>
            </ul>
          </section>

          <section className="info-section">
            <h3>Demo Login Credentials</h3>
            <div className="credentials-box">
              <div className="credential-item">
                <h4>Manager Account</h4>
                <p><strong>Username:</strong> demo_manager</p>
                <p><strong>Password:</strong> demo_manager</p>
                <p className="role-description">
                  Managers can create and manage users, components, bids, view team reports,
                  manage company settings, and generate bid packages.
                </p>
              </div>

              <div className="credential-item">
                <h4>User Account</h4>
                <p><strong>Username:</strong> demo_user</p>
                <p><strong>Password:</strong> demo_user</p>
                <p className="role-description">
                  Users can create and manage their own bids, view components, track bid submissions,
                  and access the company calendar.
                </p>
              </div>

              <div className="credential-item">
                <h4>Executive Account</h4>
                <p><strong>Username:</strong> demo_executive</p>
                <p><strong>Password:</strong> demo_executive</p>
                <p className="role-description">
                  Executives can view executive reports with monthly activity analytics,
                  access the company calendar, and view bid summaries with cost breakdowns.
                </p>
              </div>
            </div>
          </section>

          <section className="info-section">
            <h3>Getting Started</h3>
            <ol>
              <li>Log in using one of the demo accounts above</li>
              <li>Explore the Dashboard to see available features</li>
              <li>Visit the Component Library to see reusable templates</li>
              <li>Create a new bid to see the bid assembly workflow</li>
              <li>Check the Company Calendar to view bid due dates</li>
            </ol>
          </section>
        </div>

        <button
          onClick={() => navigate('/login')}
          className="back-to-login-button"
        >
          Back to Login
        </button>
      </div>
    </div>
  );
};

export default AppInfo;
